import type { Beacon } from "@kensio/rainlytics/beacon";

import {
  savedWordRequestEvents,
  type SavedWordRequestOutcome,
} from "../analytics/saved-word-request-events.js";

/** A monotonic clock that reads milliseconds. */
export interface MillisecondClock {
  /** The current reading in milliseconds. */
  now(): number;
}

/** Reports how a saved-word request ended. */
export type SavedWordRequestFinished = (
  outcome: SavedWordRequestOutcome,
) => void;

/** Starts measuring one saved-word request. */
export type SavedWordRequestStarted = () => SavedWordRequestFinished;

/** Measure one saved-word request and preserve its result or failure. */
export async function measureSavedWordRequest<Result>(
  requestStarted: SavedWordRequestStarted | undefined,
  request: () => Promise<Result>,
): Promise<Result> {
  const requestFinished = requestStarted?.();

  try {
    const result = await request();
    requestFinished?.("completed");

    return result;
  } catch (error) {
    requestFinished?.("failed");

    throw error;
  }
}

/** What the saved-word latency reporter needs. */
export interface SavedWordLatencyReporterProperties {
  /** The Rainlytics beacon that sends the measurement. */
  readonly beacon: Beacon;

  /** The monotonic clock used to measure the request. */
  readonly clock: MillisecondClock;

  /** The page where the request began. */
  readonly page: string;
}

/**
 * Measures saved-word requests and reports their duration through Rainlytics.
 *
 * The clock starts on the button press. The caller finishes the measurement
 * after the response body has been read, or when the request fails. This keeps
 * JSON transfer and parsing inside the duration users experience.
 */
export function savedWordLatencyReporter(
  properties: SavedWordLatencyReporterProperties,
): SavedWordRequestStarted {
  return () => {
    const startedAt = properties.clock.now();

    return (outcome) => {
      properties.beacon.report({
        event: savedWordRequestEvents[outcome],
        page: properties.page,
        value: properties.clock.now() - startedAt,
      });
    };
  };
}
